import { useEffect, useState, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { uploadFileSmart, UPLOAD_MAX_SIZE } from "../lib/uploads";
import { PageHeader, Empty, Modal } from "../components/ui-bits";
import DropZone from "../components/DropZone";
import { useBrandPermissions } from "../lib/brand";
import {
  Folder as FolderIcon, FolderPlus, Upload, Search, File as FileIcon,
  Image as ImageIcon, Download, Trash2, Edit3, Move, X, ChevronRight,
  History, RotateCcw, ArrowUpFromLine, MoreHorizontal, GripVertical, ChevronDown,
} from "lucide-react";

const isImg = (ct) => (ct || "").startsWith("image/");
const isPdf = (ct) => (ct || "").includes("pdf");
const fmtBytes = (n) => {
  if (!n) return "0 B";
  const units = ["B","KB","MB","GB"]; let i = 0; let x = n;
  while (x >= 1024 && i < 3) { x /= 1024; i++; }
  return `${x.toFixed(x < 10 ? 1 : 0)} ${units[i]}`;
};
// Cookie-auth: <img src> and <iframe src> use the httpOnly cookie automatically.
const authUrl = fileUrl;

export default function FolderPage() {
  const { canEdit, canAdmin } = useBrandPermissions();
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentId, setCurrentId] = useState("all"); // "all" | folder id
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("date_desc");
  const [typeFilter, setTypeFilter] = useState("all");
  const [preview, setPreview] = useState(null);
  const [newFolder, setNewFolder] = useState(false);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [versionsFor, setVersionsFor] = useState(null);   // file doc
  const [versionsData, setVersionsData] = useState(null); // {current, previous}
  const [versionsBusy, setVersionsBusy] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null); // file being replaced with new version
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, currentName: "", currentPercent: 0 });
  const [err, setErr] = useState("");
  const [menuOpen, setMenuOpen] = useState(null); // folder id whose ⋯ menu is open
  const [dragId, setDragId] = useState(null);        // id of folder being dragged
  const [dropTarget, setDropTarget] = useState(null); // {id, pos: 'before'|'after'|'inside'}
  const [collapsed, setCollapsed] = useState(new Set()); // folder ids whose children are hidden
  const fileRef = useRef();
  const versionFileRef = useRef();

  const loadFolders = () => api.get("/folders").then(r => setFolders(r.data)).catch(()=>{});
  const loadFiles = () => {
    const params = {};
    if (currentId && currentId !== "all") params.folder_id = currentId;
    else params.folder_id = "all";
    if (search) params.search = search;
    return api.get("/files", { params }).then(r => setFiles(r.data)).catch(()=>{});
  };
  useEffect(() => { loadFolders(); }, []);
  useEffect(() => { loadFiles(); }, [currentId, search]);

  // Deep-link support: `?folder=<id>` selects a folder, `?file=<id>` opens preview.
  // Triggered by the Team Chat "Apri cartella / Apri file" cards.
  const loc = useLocation();
  const openedRef = useRef({ folder: null, file: null });
  useEffect(() => {
    const p = new URLSearchParams(loc.search);
    const targetFolder = p.get("folder");
    const targetFile = p.get("file");
    if (targetFolder && openedRef.current.folder !== targetFolder && folders.some(f => f.id === targetFolder)) {
      setCurrentId(targetFolder);
      openedRef.current.folder = targetFolder;
    } else if (targetFile && !targetFolder && openedRef.current.file !== targetFile && currentId !== "all") {
      // Make sure we can find the file by scanning across the whole drive.
      setCurrentId("all");
    }
  }, [folders, loc.search, currentId]);
  useEffect(() => {
    const p = new URLSearchParams(loc.search);
    const targetFile = p.get("file");
    if (targetFile && openedRef.current.file !== targetFile) {
      const f = files.find(x => x.id === targetFile);
      if (f) {
        setPreview(f);
        openedRef.current.file = targetFile;
      }
    }
  }, [files, loc.search]);

  const current = folders.find(f => f.id === currentId);

  const filtered = useMemo(() => {
    let arr = files;
    if (typeFilter === "images") arr = arr.filter(f => isImg(f.content_type));
    else if (typeFilter === "pdfs") arr = arr.filter(f => isPdf(f.content_type));
    else if (typeFilter === "others") arr = arr.filter(f => !isImg(f.content_type) && !isPdf(f.content_type));
    const dir = sort.endsWith("desc") ? -1 : 1;
    const keyFn = sort.startsWith("name") ? (f)=>f.original_filename?.toLowerCase()
      : sort.startsWith("size") ? (f)=>f.size || 0
      : (f)=>f.created_at || "";
    return [...arr].sort((a,b) => (keyFn(a) < keyFn(b) ? -1 : keyFn(a) > keyFn(b) ? 1 : 0) * dir);
  }, [files, typeFilter, sort]);

  const uploadFiles = async (list) => {
    const files = Array.from(list || []);
    if (files.length === 0) return;
    // Client-side cap that mirrors the backend chunked ceiling — surface a
    // clear error before we even start the upload session.
    const oversized = files.find((f) => f.size > UPLOAD_MAX_SIZE);
    if (oversized) {
      const gb = (UPLOAD_MAX_SIZE / (1024 ** 3)).toFixed(0);
      setErr(`"${oversized.name}" is too large. Maximum size is ${gb} GB per file.`);
      return;
    }
    setBusy(true); setErr("");
    setProgress({ done: 0, total: files.length, currentName: "", currentPercent: 0 });
    let done = 0;
    for (const f of files) {
      setProgress({ done, total: files.length, currentName: f.name, currentPercent: 0 });
      try {
        await uploadFileSmart(
          f,
          { folder_id: currentId && currentId !== "all" ? currentId : undefined },
          {
            onProgress: (p) =>
              setProgress({ done, total: files.length, currentName: f.name, currentPercent: p.percent }),
          },
        );
      } catch (er) {
        setErr(formatApiErrorDetail(er.response?.data?.detail) || er.message);
      }
      done += 1;
      setProgress({ done, total: files.length, currentName: f.name, currentPercent: 1 });
    }
    setBusy(false);
    setProgress({ done: 0, total: 0, currentName: "", currentPercent: 0 });
    loadFiles();
  };
  const upload = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    return uploadFiles(list);
  };

  const openVersions = async (f) => {
    setVersionsFor(f); setVersionsData(null); setVersionsBusy(true);
    try {
      const { data } = await api.get(`/files/${f.id}/versions`);
      setVersionsData(data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setVersionsBusy(false); }
  };

  const uploadReplacementVersion = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !replaceTarget) return;
    if (file.size > UPLOAD_MAX_SIZE) {
      const gb = (UPLOAD_MAX_SIZE / (1024 ** 3)).toFixed(0);
      setErr(`"${file.name}" is too large. Maximum size is ${gb} GB per file.`);
      return;
    }
    setVersionsBusy(true); setErr("");
    try {
      await uploadFileSmart(file, { version_of_file_id: replaceTarget.id });
      // reload versions view + files grid
      if (versionsFor && versionsFor.id === replaceTarget.id) {
        const { data } = await api.get(`/files/${replaceTarget.id}/versions`);
        setVersionsData(data);
      }
      loadFiles();
    } catch (er) {
      setErr(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally {
      setVersionsBusy(false);
      setReplaceTarget(null);
    }
  };

  const restoreVersion = async (v) => {
    if (!versionsFor) return;
    if (!window.confirm(`Restore version ${v.version} of "${versionsFor.original_filename}"?`)) return;
    setVersionsBusy(true);
    try {
      await api.post(`/files/${versionsFor.id}/restore/${v.version}`);
      const { data } = await api.get(`/files/${versionsFor.id}/versions`);
      setVersionsData(data);
      loadFiles();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setVersionsBusy(false); }
  };

  const createFolder = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post("/folders", { name: newName.trim() });
      setNewName(""); setNewFolder(false); await loadFolders();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const renameFolder = async () => {
    if (!renaming || !newName.trim()) return;
    setBusy(true);
    try {
      await api.put(`/folders/${renaming.id}`, { name: newName.trim() });
      setRenaming(null); setNewName(""); await loadFolders();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deleteFolder = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}"?\n\nFiles inside will not be deleted — they will remain available in "All files".`)) return;
    try {
      await api.delete(`/folders/${f.id}`); loadFolders();
      if (currentId === f.id) setCurrentId("all");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Drag & drop reordering + reparenting (native HTML5)
  // Children map (parent_id -> [child folders in current order])
  const childrenByParent = useMemo(() => {
    const m = new Map();
    for (const f of folders) {
      const p = f.parent_id || null;
      if (!m.has(p)) m.set(p, []);
      m.get(p).push(f);
    }
    return m;
  }, [folders]);

  // True if `targetId` is `sourceId` itself or a descendant of it.
  const isSelfOrDescendant = (sourceId, targetId) => {
    if (!sourceId || !targetId) return false;
    if (sourceId === targetId) return true;
    const byId = new Map(folders.map(f => [f.id, f]));
    let cur = byId.get(targetId);
    const seen = new Set();
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      if (cur.parent_id === sourceId) return true;
      cur = byId.get(cur.parent_id);
    }
    return false;
  };

  const persistOrder = async (orderedFolders, parents = null) => {
    try {
      const body = { folder_ids: orderedFolders.map(f => f.id) };
      if (parents) body.parents = parents;
      await api.post("/folders/reorder", body);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail));
      loadFolders(); // roll back on failure
    }
  };

  const onFolderDrop = (targetId, pos) => {
    if (!dragId || dragId === targetId) return;
    if (isSelfOrDescendant(dragId, targetId)) return;
    const target = folders.find(f => f.id === targetId);
    const dragged = folders.find(f => f.id === dragId);
    if (!target || !dragged) return;

    // Deep-copy the child map as arrays of ids
    const cmap = new Map();
    for (const [k, arr] of childrenByParent) cmap.set(k, arr.map(f => f.id));
    // Remove dragged from its current parent's children
    const curParent = dragged.parent_id || null;
    cmap.set(curParent, (cmap.get(curParent) || []).filter(id => id !== dragId));

    let newParent;
    let insertIdx;
    if (pos === "inside") {
      newParent = targetId;
      if (!cmap.has(newParent)) cmap.set(newParent, []);
      insertIdx = 0; // insert at top of the target's children
    } else {
      newParent = target.parent_id || null;
      if (!cmap.has(newParent)) cmap.set(newParent, []);
      const siblings = cmap.get(newParent);
      const tIdx = siblings.indexOf(targetId);
      insertIdx = pos === "before" ? Math.max(0, tIdx) : tIdx + 1;
    }
    cmap.get(newParent).splice(insertIdx, 0, dragId);

    // DFS to produce new flat list and parents map
    const byId = new Map(folders.map(f => [f.id, f]));
    const newFlat = [];
    const parents = { [dragId]: newParent };
    const visit = (id, parentId) => {
      const f = byId.get(id);
      if (!f) return;
      newFlat.push({ ...f, parent_id: parentId });
      for (const cid of cmap.get(id) || []) visit(cid, id);
    };
    for (const rid of cmap.get(null) || []) visit(rid, null);

    setFolders(newFlat);
    // Auto-expand the destination folder so the user sees the moved item
    if (pos === "inside") {
      setCollapsed(prev => { const n = new Set(prev); n.delete(targetId); return n; });
    }
    persistOrder(newFlat, parents);
  };

  const toggleCollapsed = (id) => {
    setCollapsed(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const moveFile = async (fileId, folderId) => {
    try {
      await api.patch(`/files/${fileId}`, { folder_id: folderId });
      loadFiles();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const renameFile = async (f) => {
    const name = window.prompt("Rename file:", f.original_filename);
    if (!name || name === f.original_filename) return;
    try {
      await api.patch(`/files/${f.id}`, { original_filename: name });
      loadFiles();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Delete "${f.original_filename}"?`)) return;
    try { await api.delete(`/files/${f.id}`); loadFiles(); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="folder-page">
      <PageHeader eyebrow="Archive" title="Brand Folder"
        description="Central drive for all files in this brand — private to your workspace."
        action={canEdit && (
          <div className="flex items-center gap-2">
            <button onClick={()=>{setNewFolder(true); setNewName("");}} className="btn-ghost inline-flex items-center gap-2" data-testid="new-folder-btn">
              <FolderPlus size={14}/> New Folder
            </button>
            <button onClick={()=>fileRef.current?.click()} className="btn-gold inline-flex items-center gap-2" data-testid="folder-upload-btn">
              <Upload size={14}/> Upload
            </button>
            <input ref={fileRef} type="file" multiple hidden onChange={upload}/>
          </div>
        )}
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Sidebar folder list */}
        <aside className="lg:col-span-1 card-dark p-3 h-fit sticky top-4" data-testid="folder-sidebar">
          <button
            onClick={()=>setCurrentId("all")}
            className={`w-full text-left px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2 ${currentId === "all" ? "text-[#0A0A0A] bg-[#0A0A0A]/10" : "text-[#525252] hover:text-[#0A0A0A]"}`}
            data-testid="folder-all"
          >
            <FolderIcon size={12}/> All files
          </button>
          <div className="mt-3 space-y-0.5 max-h-[520px] overflow-y-auto">
            {(childrenByParent.get(null) || []).map(f => renderFolderNode(f, 0, {
              childrenByParent, dragId, dropTarget, setDropTarget, setDragId,
              collapsed, toggleCollapsed, currentId, setCurrentId, canEdit, canAdmin,
              menuOpen, setMenuOpen, setRenaming, setNewName, deleteFolder,
              isSelfOrDescendant, onFolderDrop,
            }))}
          </div>
        </aside>

        {/* File grid */}
        <section className="lg:col-span-4">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="text-xs text-[#525252] uppercase tracking-widest flex items-center gap-2">
              <FolderIcon size={12} style={{ color: current?.color || "#0A0A0A" }}/> {current ? current.name : "All files"}
              <span className="text-[#737373]">· {filtered.length} items</span>
            </div>
            <div className="flex-1"/>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-2.5 text-[#737373]"/>
              <input placeholder="Search files…" className="input-dark pl-7 py-1 text-xs w-52" value={search} onChange={(e)=>setSearch(e.target.value)} data-testid="folder-search"/>
            </div>
            <select className="input-dark py-1 text-xs" value={typeFilter} onChange={(e)=>setTypeFilter(e.target.value)} data-testid="folder-type-filter">
              <option value="all">All types</option>
              <option value="images">Images</option>
              <option value="pdfs">PDFs</option>
              <option value="others">Others</option>
            </select>
            <select className="input-dark py-1 text-xs" value={sort} onChange={(e)=>setSort(e.target.value)}>
              <option value="date_desc">Newest first</option>
              <option value="date_asc">Oldest first</option>
              <option value="name_asc">Name A→Z</option>
              <option value="name_desc">Name Z→A</option>
              <option value="size_desc">Largest first</option>
            </select>
          </div>

          {err && <div className="text-red-600 text-sm mb-3">{err}</div>}
          {busy && progress.total > 0 && (
            <div className="mb-3" data-testid="folder-upload-progress">
              <div className="flex items-center justify-between text-xs text-[#0A0A0A] mb-1">
                <span className="truncate mr-3">
                  Uploading {progress.done + 1}/{progress.total}
                  {progress.currentName ? ` — ${progress.currentName}` : "…"}
                </span>
                <span className="tabular-nums text-[#525252]">
                  {Math.round((progress.currentPercent || 0) * 100)}%
                </span>
              </div>
              <div className="h-1 bg-[#E5E5E5] overflow-hidden">
                <div
                  className="h-full bg-[#0A0A0A] transition-[width] duration-200"
                  style={{ width: `${Math.min(100, Math.round((progress.currentPercent || 0) * 100))}%` }}
                />
              </div>
            </div>
          )}

          <DropZone
            onFiles={canEdit ? uploadFiles : null}
            disabled={!canEdit}
            testId="folder-dropzone"
            accept="images, videos, PDFs — up to 2GB each"
            overlayLabel={current ? `Drop into "${current.name}"` : "Drop to upload"}
          >
          {filtered.length === 0 ? (
            <Empty title="This folder is empty" hint={canEdit ? "Drag files here to upload, or click Upload." : "No files yet."}/>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="folder-grid">
              {filtered.map(f => (
                <div key={f.id} className="card-dark overflow-hidden group" data-testid={`folder-file-${f.id}`}>
                  <button onClick={()=>setPreview(f)} className="w-full aspect-[4/3] bg-[#FAFAFA] flex items-center justify-center">
                    {isImg(f.content_type) ? (
                      <img src={authUrl(f.id, "thumb")} alt="" className="w-full h-full object-cover" loading="lazy"/>
                    ) : isPdf(f.content_type) ? (
                      <FileIcon size={40} className="text-red-600/70"/>
                    ) : (
                      <FileIcon size={40} className="text-[#737373]"/>
                    )}
                  </button>
                  <div className="p-2">
                    <div className="text-xs text-[#0A0A0A] truncate flex items-center gap-1" title={f.original_filename}>
                      <span className="truncate">{f.original_filename}</span>
                      {(f.version || 1) > 1 && (
                        <span
                          className="text-[9px] text-[#0A0A0A] border border-[#0A0A0A]/40 px-1 rounded-sm"
                          title={`Version ${f.version}`}
                          data-testid={`folder-file-version-badge-${f.id}`}
                        >v{f.version}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-[#737373] flex items-center justify-between mt-1">
                      <span>{fmtBytes(f.size)}</span>
                      <span>{new Date(f.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <a href={authUrl(f.id)} download={f.original_filename} className="text-[#525252] hover:text-[#0A0A0A]"><Download size={12}/></a>
                      {canEdit && (
                        <>
                          <select className="input-dark py-0.5 text-[10px] flex-1 mx-2" value={f.folder_id || ""} onChange={(e)=>moveFile(f.id, e.target.value)} title="Move">
                            <option value="">— unfiled —</option>
                            {folders.map(fd => <option key={fd.id} value={fd.id}>{fd.name}</option>)}
                          </select>
                          <button onClick={()=>openVersions(f)} className="text-[#525252] hover:text-[#0A0A0A]" title="Version history" data-testid={`folder-file-versions-${f.id}`}><History size={12}/></button>
                          <button onClick={()=>renameFile(f)} className="text-[#525252] hover:text-[#0A0A0A]" title="Rename"><Edit3 size={12}/></button>
                          <button onClick={()=>deleteFile(f)} className="text-[#525252] hover:text-red-600" title="Delete"><Trash2 size={12}/></button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </DropZone>
        </section>
      </div>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-8" onClick={()=>setPreview(null)}>
          <button className="absolute top-4 right-4 text-[#0A0A0A] hover:text-[#0A0A0A]" onClick={()=>setPreview(null)}><X size={24}/></button>
          <div className="max-w-5xl max-h-full flex flex-col items-center gap-4" onClick={(e)=>e.stopPropagation()}>
            {isImg(preview.content_type) ? (
              <img src={authUrl(preview.id, "preview")} className="max-h-[80vh] object-contain"/>
            ) : isPdf(preview.content_type) ? (
              <iframe src={authUrl(preview.id)} title="pdf" className="w-[80vw] h-[80vh] bg-white"/>
            ) : (
              <div className="text-[#0A0A0A] text-sm">No inline preview for this file type.</div>
            )}
            <div className="flex items-center gap-4">
              <div className="text-xs text-[#0A0A0A]">{preview.original_filename}</div>
              <a href={authUrl(preview.id)} download={preview.original_filename} className="btn-gold text-xs inline-flex items-center gap-2"><Download size={12}/> Download</a>
            </div>
          </div>
        </div>
      )}

      <Modal open={newFolder} onClose={()=>setNewFolder(false)} title="New Folder">
        <div className="space-y-3">
          <input autoFocus placeholder="Folder name" className="input-dark" value={newName} onChange={(e)=>setNewName(e.target.value)} data-testid="new-folder-name" onKeyDown={(e)=>e.key==="Enter"&&createFolder()}/>
          <div className="flex justify-end gap-2">
            <button onClick={()=>setNewFolder(false)} className="btn-ghost">Cancel</button>
            <button onClick={createFolder} disabled={busy || !newName.trim()} className="btn-gold" data-testid="new-folder-save">Create</button>
          </div>
        </div>
      </Modal>

      <Modal open={!!renaming} onClose={()=>setRenaming(null)} title={`Rename "${renaming?.name}"`}>
        <div className="space-y-3">
          <input autoFocus className="input-dark" value={newName} onChange={(e)=>setNewName(e.target.value)}/>
          <div className="flex justify-end gap-2">
            <button onClick={()=>setRenaming(null)} className="btn-ghost">Cancel</button>
            <button onClick={renameFolder} disabled={busy} className="btn-gold">Rename</button>
          </div>
        </div>
      </Modal>

      {/* File version history */}
      <Modal
        open={!!versionsFor}
        onClose={()=>{ setVersionsFor(null); setVersionsData(null); }}
        title={versionsFor ? `Version history — ${versionsFor.original_filename}` : ""}
      >
        <div className="space-y-3" data-testid="versions-modal">
          {versionsBusy && <div className="text-xs text-[#525252]">Working…</div>}
          {versionsData && (
            <>
              {/* Current version */}
              <div className="border border-[#0A0A0A]/40 p-3" data-testid="versions-current">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-[#0A0A0A] uppercase tracking-widest mb-1">Current · v{versionsData.current.version}</div>
                    <div className="text-sm text-[#0A0A0A]">{versionsData.current.original_filename}</div>
                    <div className="text-[10px] text-[#737373]">
                      {fmtBytes(versionsData.current.size)} · {versionsData.current.content_type}
                      {versionsData.current.created_at && <> · {new Date(versionsData.current.created_at).toLocaleString()}</>}
                    </div>
                  </div>
                  <a href={authUrl(versionsFor.id)} download={versionsData.current.original_filename} className="text-[#525252] hover:text-[#0A0A0A]" title="Download current"><Download size={14}/></a>
                </div>
              </div>

              {/* Previous versions */}
              {versionsData.previous && versionsData.previous.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {[...versionsData.previous].sort((a,b)=>(b.version||0)-(a.version||0)).map(v => (
                    <div key={v.version} className="border border-[#E5E5E5] p-3 flex items-center justify-between" data-testid={`versions-previous-${v.version}`}>
                      <div className="min-w-0">
                        <div className="text-[10px] text-[#525252] uppercase tracking-widest mb-1">v{v.version}</div>
                        <div className="text-sm text-[#0A0A0A] truncate">{v.original_filename}</div>
                        <div className="text-[10px] text-[#737373]">
                          {fmtBytes(v.size)} · {v.content_type}
                          {v.created_at && <> · {new Date(v.created_at).toLocaleString()}</>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-3 shrink-0">
                        <button
                          onClick={()=>restoreVersion(v)}
                          disabled={versionsBusy}
                          className="text-xs text-[#0A0A0A] hover:text-[#171717] inline-flex items-center gap-1 uppercase tracking-widest"
                          data-testid={`versions-restore-${v.version}`}
                        >
                          <RotateCcw size={12}/> Restore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-[#737373]" data-testid="versions-empty">No previous versions yet — upload a replacement below to start version history.</div>
              )}

              {/* Upload new version */}
              {canEdit && (
                <div className="pt-3 border-t border-[#E5E5E5] flex items-center justify-between">
                  <div className="text-xs text-[#525252]">Uploading a replacement will bump this file to v{(versionsData.current.version || 1) + 1}. The current version becomes history.</div>
                  <button
                    onClick={()=>{ setReplaceTarget(versionsFor); versionFileRef.current?.click(); }}
                    disabled={versionsBusy}
                    className="btn-gold inline-flex items-center gap-2 shrink-0 ml-3"
                    data-testid="versions-replace-btn"
                  >
                    <ArrowUpFromLine size={12}/> Upload new version
                  </button>
                </div>
              )}
            </>
          )}
          <input ref={versionFileRef} type="file" hidden onChange={uploadReplacementVersion}/>
        </div>
      </Modal>
    </div>
  );
}


// Recursive folder row renderer. Kept outside the component so React
// treats it as a stable helper — state comes in via the `ctx` bag.
function renderFolderNode(f, depth, ctx) {
  const {
    childrenByParent, dragId, dropTarget, setDropTarget, setDragId,
    collapsed, toggleCollapsed, currentId, setCurrentId, canEdit, canAdmin,
    menuOpen, setMenuOpen, setRenaming, setNewName, deleteFolder,
    isSelfOrDescendant, onFolderDrop,
  } = ctx;
  const children = childrenByParent.get(f.id) || [];
  const hasChildren = children.length > 0;
  const isCollapsed = collapsed.has(f.id);
  const isDragging = dragId === f.id;
  const isTarget = dropTarget?.id === f.id;
  const invalidTarget = dragId && isSelfOrDescendant(dragId, f.id);
  return (
    <div key={f.id} data-testid={`folder-node-${f.id}`}>
      <div
        data-testid={`folder-item-${f.id}`}
        draggable={canEdit}
        onDragStart={(e) => {
          if (!canEdit) return;
          setDragId(f.id);
          e.dataTransfer.effectAllowed = "move";
          try { e.dataTransfer.setData("text/plain", f.id); } catch {}
        }}
        onDragOver={(e) => {
          if (!dragId || dragId === f.id || invalidTarget) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          const r = e.currentTarget.getBoundingClientRect();
          const rel = (e.clientY - r.top) / r.height;
          const pos = rel < 0.25 ? "before" : rel > 0.75 ? "after" : "inside";
          if (dropTarget?.id !== f.id || dropTarget?.pos !== pos) setDropTarget({ id: f.id, pos });
        }}
        onDragLeave={() => {
          if (dropTarget?.id === f.id) setDropTarget(null);
        }}
        onDrop={(e) => {
          e.preventDefault();
          const pos = dropTarget?.pos || "before";
          onFolderDrop(f.id, pos);
          setDragId(null); setDropTarget(null);
        }}
        onDragEnd={() => { setDragId(null); setDropTarget(null); }}
        style={{ paddingLeft: 8 + depth * 14 }}
        className={`group relative flex items-center justify-between pr-2 py-1.5 text-xs cursor-pointer
          ${currentId === f.id ? "bg-[#0A0A0A]/10" : "hover:bg-black/[0.03]"}
          ${isDragging ? "opacity-40" : ""}
          ${isTarget && dropTarget?.pos === "inside" ? "ring-2 ring-inset ring-[#0A0A0A] bg-[#0A0A0A]/5" : ""}`}
      >
        {isTarget && dropTarget?.pos === "before" && (
          <div className="absolute left-0 right-0 -top-px h-0.5 bg-[#0A0A0A] pointer-events-none z-10" data-testid={`drop-indicator-before-${f.id}`}/>
        )}
        {isTarget && dropTarget?.pos === "after" && (
          <div className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#0A0A0A] pointer-events-none z-10" data-testid={`drop-indicator-after-${f.id}`}/>
        )}

        {/* Expand/collapse toggle (or spacer) */}
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); toggleCollapsed(f.id); }}
            className="text-[#525252] hover:text-[#0A0A0A] mr-0.5 shrink-0 p-0.5"
            data-testid={`folder-toggle-${f.id}`}
            aria-label={isCollapsed ? "Expand" : "Collapse"}
          >
            {isCollapsed ? <ChevronRight size={11}/> : <ChevronDown size={11}/>}
          </button>
        ) : (
          <span className="inline-block w-4 shrink-0"/>
        )}

        {canEdit && (
          <span
            className="text-[#a3a3a3] group-hover:text-[#525252] cursor-grab active:cursor-grabbing mr-1 shrink-0"
            data-testid={`folder-grip-${f.id}`}
            title="Drag to reorder or nest into another folder"
          >
            <GripVertical size={12}/>
          </span>
        )}
        <button className="flex items-center gap-2 min-w-0 text-left flex-1" onClick={()=>setCurrentId(f.id)} data-testid={`folder-${f.slug || f.id}`}>
          <FolderIcon size={12} style={{ color: f.color || "#0A0A0A" }}/>
          <span className="truncate text-[#0A0A0A]">{f.name}</span>
          {f.is_default && <span className="text-[9px] text-[#737373] uppercase tracking-widest">std</span>}
        </button>
        {canEdit && (
          <div className={`${menuOpen === f.id ? "flex" : "hidden group-hover:flex"} items-center relative`}>
            <button
              onClick={(e)=>{e.stopPropagation(); setMenuOpen(menuOpen === f.id ? null : f.id);}}
              className="text-[#525252] hover:text-[#0A0A0A] p-0.5"
              data-testid={`folder-menu-btn-${f.id}`}
              aria-label="Folder actions"
            >
              <MoreHorizontal size={12}/>
            </button>
            {menuOpen === f.id && (
              <>
                <div className="fixed inset-0 z-40" onClick={()=>setMenuOpen(null)}/>
                <div className="absolute right-0 top-full mt-1 z-50 min-w-[140px] bg-white border border-[#E5E5E5] shadow-md py-1"
                  data-testid={`folder-menu-${f.id}`}
                  onClick={(e)=>e.stopPropagation()}>
                  <button
                    onClick={()=>{ setMenuOpen(null); setRenaming(f); setNewName(f.name); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-[#0A0A0A] hover:bg-[#FAFAFA] flex items-center gap-2"
                    data-testid={`folder-menu-rename-${f.id}`}
                  >
                    <Edit3 size={11}/> Rename
                  </button>
                  {canAdmin && (
                    <button
                      onClick={()=>{ setMenuOpen(null); deleteFolder(f); }}
                      className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                      data-testid={`folder-menu-delete-${f.id}`}
                    >
                      <Trash2 size={11}/> Delete
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {hasChildren && !isCollapsed && (
        <div data-testid={`folder-children-${f.id}`}>
          {children.map(c => renderFolderNode(c, depth + 1, ctx))}
        </div>
      )}
    </div>
  );
}
